import React, { useState } from "react";
import { Alert, Box, Button, Container, Stack, TextField, Typography } from "@mui/material";
import { saveFoundingFamilyEmail } from "../../services/foundingFamilyService";
import { landingColors } from "../../assets/theme/landingTheme";

const LandingSignupSection = ({ variant = "desktop" }) => {
  const isTablet = variant === "tablet";
  const [email, setEmail] = useState("");
  const [submitError, setSubmitError] = useState("");
  const [submitSuccess, setSubmitSuccess] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleSubmit = async (event) => {
    event.preventDefault();
    const trimmedEmail = email.trim();

    if (!trimmedEmail) {
      setSubmitError("Enter an email address first.");
      setSubmitSuccess("");
      return;
    }

    try {
      setIsSubmitting(true);
      setSubmitError("");
      await saveFoundingFamilyEmail(trimmedEmail);
      setSubmitSuccess("You're in. We'll be in touch.");
      setEmail("");
    } catch {
      setSubmitSuccess("");
      setSubmitError("Could not save your email. Please try again.");
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Box
      sx={{
        px: { xs: 2, md: isTablet ? 3 : 4 },
        py: { xs: 2.6, md: isTablet ? 3 : 3.3 },
      }}
    >
      <Container maxWidth="lg">
        <Box
          sx={{
            maxWidth: 760,
            mx: "auto",
            textAlign: "center",
            pt: { xs: 0.5, md: 0.75 },
          }}
        >
          <Box sx={{ maxWidth: 620, mx: "auto" }}>
            <Typography
              sx={{
                fontSize: { xs: "0.74rem", md: "0.78rem" },
                fontWeight: 800,
                letterSpacing: "0.16em",
                textTransform: "uppercase",
                color: landingColors.cyanPop,
                mb: 0.9,
              }}
            >
              Join the Founding Family
            </Typography>
            <Typography
              sx={{
                fontFamily: "'Outfit', sans-serif",
                fontSize: { xs: "1.45rem", md: isTablet ? "1.6rem" : "1.75rem" },
                lineHeight: 1.1,
                letterSpacing: "-0.04em",
                fontWeight: 800,
                color: landingColors.heroText,
                mb: 1,
                maxWidth: 520,
                mx: "auto",
              }}
            >
              Be among the first families.
            </Typography>
            <Typography
              sx={{
                fontSize: { xs: "0.92rem", md: "0.96rem" },
                lineHeight: 1.55,
                color: landingColors.bodyText,
                maxWidth: 540,
                mx: "auto",
                mb: 0.25,
              }}
            >
              Early access, shaped by your feedback. No spam, ever.
            </Typography>

            <Box component="form" onSubmit={handleSubmit} sx={{ maxWidth: 560, width: "100%", mx: "auto", mt: 2.1 }}>
              <Stack spacing={1.05}>
                {submitSuccess ? <Alert severity="success" sx={{ py: 0.25, textAlign: "left" }}>{submitSuccess}</Alert> : null}
                {submitError ? <Alert severity="error" sx={{ py: 0.25, textAlign: "left" }}>{submitError}</Alert> : null}
                <Stack direction={{ xs: "column", sm: "row" }} spacing={1}>
                  <TextField
                    type="email"
                    placeholder="Enter your email"
                    value={email}
                    onChange={(event) => setEmail(event.target.value)}
                    fullWidth
                    size="small"
                    sx={{
                      "& .MuiOutlinedInput-root": {
                        height: 46,
                        borderRadius: "12px",
                        bgcolor: landingColors.surface,
                      },
                      "& .MuiOutlinedInput-input": {
                        py: 0,
                        px: 1.75,
                        height: "100%",
                        boxSizing: "border-box",
                      },
                    }}
                    InputProps={{
                      sx: {
                        backgroundColor: landingColors.surface,
                        borderRadius: "12px",
                        height: 46,
                        fontSize: "0.9rem",
                        "& fieldset": { borderColor: landingColors.borderMedium },
                      },
                    }}
                  />
                  <Button
                    variant="contained"
                    type="submit"
                    disabled={isSubmitting}
                    sx={{
                      height: 46,
                      minWidth: { xs: "100%", sm: 104 },
                      px: 2.25,
                      borderRadius: "12px",
                      bgcolor: landingColors.sageDeep,
                      color: "#FFFFFF",
                      textTransform: "none",
                      fontWeight: 700,
                      boxShadow: `0 8px 18px ${landingColors.shadowHero}`,
                      "&:hover": {
                        bgcolor: "#5C9683",
                      },
                    }}
                  >
                    {isSubmitting ? "..." : "Join"}
                  </Button>
                </Stack>
              </Stack>
            </Box>
          </Box>
        </Box>
      </Container>
    </Box>
  );
};

export default LandingSignupSection;
